import classNames from 'classnames';
import FJUtil from '../../../util/FJUtil';
import styles from './FJTrimDragBarBox.module.less';
import { useRef, useCallback, useState } from 'react';

const FJTrimDragBarBox = props => {
    const { relativeRect, changeRelativeRect, maxRelativeWidth, minRelativeWidth, progress, changeProgress, duration } =
        props;

    const [isStateDragging, setIsStateDragging] = useState(false);

    const isDragging = useRef(false);
    const dragType = useRef(null);
    const trimBoxContainerRef = useRef(null);

    const onMouseMove = useCallback(
        event => {
            if (!isDragging.current || !trimBoxContainerRef.current) return;

            const trimBoxContainerRect = trimBoxContainerRef.current.getBoundingClientRect();

            const deltaX = event.clientX - trimBoxContainerRect.x;

            const rect = {
                left: relativeRect.left * trimBoxContainerRect.width,
                top: relativeRect.top * trimBoxContainerRect.height,
                width: relativeRect.width * trimBoxContainerRect.width,
                height: relativeRect.height * trimBoxContainerRect.height,
            };

            switch (dragType.current) {
                case 'top': {
                    let newLeft = (deltaX - rect.width / 2) / trimBoxContainerRect.width;

                    const minLeft = 0;
                    const maxLeft = 1 - relativeRect.width;

                    if (newLeft < minLeft) {
                        newLeft = minLeft;
                    }
                    if (newLeft > maxLeft) {
                        newLeft = maxLeft;
                    }

                    changeRelativeRect(
                        {
                            ...relativeRect,
                            left: newLeft,
                        },
                        dragType.current,
                    );
                    break;
                }
                case 'left': {
                    let newLeft = deltaX / trimBoxContainerRect.width;

                    const minLeft = Math.max(relativeRect.left + relativeRect.width - maxRelativeWidth, 0);
                    const maxLeft = relativeRect.left + relativeRect.width - minRelativeWidth;

                    if (newLeft < minLeft) {
                        newLeft = minLeft;
                    }
                    if (newLeft > maxLeft) {
                        newLeft = maxLeft;
                    }

                    changeRelativeRect(
                        {
                            ...relativeRect,
                            left: newLeft,
                            width: relativeRect.width - (newLeft - relativeRect.left),
                        },
                        dragType.current,
                    );
                    break;
                }
                case 'right': {
                    let newRight = deltaX / trimBoxContainerRect.width;

                    const minRight = relativeRect.left + minRelativeWidth;
                    const maxRight = Math.min(relativeRect.left + maxRelativeWidth, 1);

                    if (newRight < minRight) {
                        newRight = minRight;
                    }
                    if (newRight > maxRight) {
                        newRight = maxRight;
                    }

                    changeRelativeRect(
                        {
                            ...relativeRect,
                            width: newRight - relativeRect.left,
                        },
                        dragType.current,
                    );
                    break;
                }
                case 'center': {
                    let newProgress = deltaX / trimBoxContainerRect.width;

                    const minProgress = relativeRect.left;
                    const maxProgress = relativeRect.left + relativeRect.width;

                    if (newProgress < minProgress) {
                        newProgress = minProgress;
                    }
                    if (newProgress > maxProgress) {
                        newProgress = maxProgress;
                    }

                    changeProgress(newProgress, 'mousemove');
                    break;
                }
            }
        },
        [isDragging, dragType, relativeRect, changeRelativeRect],
    );

    const onMouseUp = useCallback(() => {
        if (dragType.current === 'right') {
            changeProgress(relativeRect.left, 'mouseup');
        } else {
            changeProgress(null, 'mouseup');
        }

        isDragging.current = false;
        dragType.current = null;
        document.body.style.cursor = 'default';
        setIsStateDragging(false);
        window.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('mousemove', onMouseMove);
    }, [onMouseMove]);

    const onMouseDown = useCallback(
        (type = 'center', event) => {
            event.preventDefault();
            event.stopPropagation();
            isDragging.current = true;
            setIsStateDragging(true);

            dragType.current = type;
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);

            if (type === 'left') {
                document.body.style.cursor = 'w-resize';
            } else if (type === 'right') {
                document.body.style.cursor = 'e-resize';
            } else if (type === 'top') {
                document.body.style.cursor = 'grab';
            }

            if (type === 'center') {
                const trimBoxContainerRect = trimBoxContainerRef.current.getBoundingClientRect();

                const deltaX = event.clientX - trimBoxContainerRect.x;

                const newProgress = deltaX / trimBoxContainerRect.width;

                changeProgress(newProgress, 'mousedown');
            } else if (type === 'left' || type === 'top') {
                changeProgress(relativeRect.left, 'mousedown');
            } else if (type === 'right') {
                changeProgress(relativeRect.left + relativeRect.width, 'mousedown');
            }
        },
        [onMouseMove, onMouseUp],
    );

    return (
        <div className={styles.trim_box_container} ref={trimBoxContainerRef}>
            <div
                className={classNames(styles.progress_line, {
                    [styles.is_dragging]: isStateDragging,
                })}
                style={{
                    left: progress * 100 + '%',
                }}
            >
                <div className={styles.current_time}>{FJUtil.formatTime(progress * duration, false, 2)}</div>
            </div>

            <div
                className={styles.trim_box}
                onMouseDown={event => onMouseDown('center', event)}
                style={{
                    left: relativeRect.left * 100 + '%',
                    top: relativeRect.top * 100 + '%',
                    width: relativeRect.width * 100 + '%',
                    height: relativeRect.height * 100 + '%',
                }}
            >
                <div className={styles.drag_top_bar} onMouseDown={e => onMouseDown('top', e)}>
                    <div className={styles.drag_top_bar_line}></div>
                    <div className={styles.drag_top_bar_line}></div>
                    <div className={styles.drag_top_bar_line}></div>
                </div>

                <div className={styles.drag_left_bar} onMouseDown={e => onMouseDown('left', e)}>
                    <div className={styles.drag_left_bar_line}></div>
                </div>
                <div className={styles.drag_right_bar} onMouseDown={e => onMouseDown('right', e)}>
                    <div className={styles.drag_right_bar_line}></div>
                </div>

                <div className={styles.mask_box}>{FJUtil.formatTime(relativeRect.width * duration, false, 2)}</div>
            </div>
        </div>
    );
};

export default FJTrimDragBarBox;
